import { createContext, useContext, useEffect, useState, useMemo, type ReactNode } from 'react';
import { getRealTimeCollection } from '../config/data/firebase';
import { collections } from '../config/data/collections';
import { emailTemplates as staticEmailTemplates } from '../config/content/emailTemplates';

export interface EmailTemplateContextValue {
	templates: unknown[];
	loading: boolean;
}

type TemplateRecord = { id: string; key?: string; [key: string]: unknown };

const staticTemplateList: TemplateRecord[] = Object.entries(staticEmailTemplates).map(([id, template]) => ({
	id,
	key: id,
	isSystem: true,
	...template,
}));

/** Firestore wins on conflict; static seed fills gaps (e.g. rules/cache outages). */
export const mergeEmailTemplates = (remote: unknown[] | null | undefined): TemplateRecord[] => {
	const byId = new Map<string, TemplateRecord>();
	for (const template of staticTemplateList) {
		byId.set(template.id, { ...template });
	}
	for (const entry of remote || []) {
		if (!entry || typeof entry !== 'object') continue;
		const row = entry as TemplateRecord;
		const id = String(row.id || row.key || '');
		if (!id) continue;
		byId.set(id, { ...(byId.get(id) || {}), ...row, id });
	}
	return Array.from(byId.values());
};

const EmailTemplateContext = createContext<EmailTemplateContextValue | undefined>(undefined);

export const EmailTemplateProvider = ({ children }: { children: ReactNode }) => {
	const [templates, setTemplates] = useState<unknown[]>(staticTemplateList);
	const [loading, setLoading] = useState<boolean>(true);

	useEffect(() => {
		const unsubscribe = getRealTimeCollection(
			collections.emailTemplates,
			(data) => {
				try {
					setTemplates(mergeEmailTemplates(data as unknown[]));
					setLoading(false);
				} catch (error) {
					console.error('Error setting email templates:', error);
					setTemplates(staticTemplateList);
					setLoading(false);
				}
			},
			(error) => {
				console.error('emailTemplates listener failed; using static seed', error);
				setTemplates(staticTemplateList);
				setLoading(false);
			}
		);

		return () => {
			if (unsubscribe) unsubscribe();
		};
	}, []);

	const value = useMemo<EmailTemplateContextValue>(() => ({ templates, loading }), [templates, loading]);

	return <EmailTemplateContext.Provider value={value}>{children}</EmailTemplateContext.Provider>;
};

export const useEmailTemplates = (): EmailTemplateContextValue => {
	const context = useContext(EmailTemplateContext);
	if (context === undefined) {
		throw new Error('useEmailTemplates must be used within an EmailTemplateProvider');
	}
	return context;
};
